import { ROLES, type Role, type TenantType } from './enums.js';

/** Resursi un darbības RBAC matricai (§6). */
export type Resource =
  | 'tenant'
  | 'user'
  | 'product'
  | 'customer'
  | 'loading_address'
  | 'end_customer_order'
  | 'supply_order'
  | 'document'
  | 'invoice'
  | 'pricing'
  | 'claim'
  | 'task'
  | 'audit_log'
  | 'registry'
  | 'compliance'
  // Automation v2 resursi (Phase 1 substrāts).
  | 'offer' // RFQ/piedāvājumu dzinis
  | 'carrier' // pārvadātāja profils + norīkojumi (4th party)
  | 'terms' // per-tenant profila noteikumi (komerciālie + juridiskie)
  | 'cost_component'; // izmaksu komponentes (product…margin…tax)

export type Action = 'create' | 'read' | 'list' | 'update' | 'delete' | 'confirm' | 'manage';

type Matrix = Partial<Record<Resource, ReadonlyArray<Action>>>;

const ALL: ReadonlyArray<Action> = [
  'create',
  'read',
  'list',
  'update',
  'delete',
  'confirm',
  'manage',
];

/**
 * Bāzes lomu→atļauju matrica. Lauku līmeņa redakciju skat. REDACTIONS.
 * Per-tenant izņēmumus glabā D1 (permission_overrides) — pārklāj šo S2+.
 */
const ALL_RESOURCES: Resource[] = [
  'tenant',
  'user',
  'product',
  'customer',
  'loading_address',
  'end_customer_order',
  'supply_order',
  'document',
  'invoice',
  'pricing',
  'claim',
  'task',
  'audit_log',
  'registry',
  'compliance',
  'offer',
  'carrier',
  'terms',
  'cost_component',
];

const MATRIX: Record<Role, Matrix> = {
  // Super admins — pilnas atļaujas uz visiem resursiem.
  platform_super: Object.fromEntries(ALL_RESOURCES.map((r) => [r, ALL])) as Matrix,
  platform_admin: {
    tenant: ALL,
    user: ALL,
    product: ALL,
    customer: ALL,
    loading_address: ALL,
    end_customer_order: ALL,
    supply_order: ALL,
    document: ALL,
    invoice: ALL,
    pricing: ALL,
    claim: ALL,
    task: ALL,
    audit_log: ['read', 'list'],
    registry: ALL,
    compliance: ALL,
    offer: ALL,
    carrier: ALL,
    terms: ALL,
    cost_component: ALL,
  },
  platform_ops: {
    product: ['read', 'list', 'update'],
    customer: ['create', 'read', 'list', 'update'],
    loading_address: ['create', 'read', 'list', 'update'],
    end_customer_order: ['create', 'read', 'list', 'update', 'confirm'],
    supply_order: ['create', 'read', 'list', 'update'],
    document: ['create', 'read', 'list'],
    claim: ['create', 'read', 'list', 'update'],
    task: ALL,
    registry: ['read', 'list', 'update'],
    // b2b admin komponē piedāvājumus + norīko pārvadātājus (§ automation Phase 2/3).
    offer: ['create', 'read', 'list', 'update', 'confirm'],
    carrier: ['create', 'read', 'list', 'update'],
    terms: ['read', 'list', 'update'],
    cost_component: ['create', 'read', 'list', 'update'],
  },
  platform_finance: {
    invoice: ALL,
    pricing: ALL,
    end_customer_order: ['read', 'list'],
    supply_order: ['read', 'list'],
    document: ['read', 'list'],
    customer: ['read', 'list'],
    task: ['read', 'list', 'update'],
    offer: ['read', 'list'],
    terms: ['read', 'list', 'update'],
    cost_component: ['create', 'read', 'list', 'update'],
  },
  supplier_admin: {
    user: ['create', 'read', 'list', 'update'],
    supply_order: ['read', 'list', 'confirm', 'update'],
    document: ['create', 'read', 'list'],
    loading_address: ['create', 'read', 'list', 'update'],
    claim: ['read', 'list', 'update'],
    task: ['read', 'list', 'update'],
    // Piegādātājs var pievienot/norīkot pārvadātāju, kad incoterm liek tam organizēt transportu (§3).
    carrier: ['create', 'read', 'list', 'update'],
    offer: ['read', 'list'],
    terms: ['read', 'list'],
  },
  supplier_ops: {
    supply_order: ['read', 'list', 'confirm'],
    document: ['create', 'read', 'list'],
    task: ['read', 'list', 'update'],
    carrier: ['read', 'list'],
  },
  customer_admin: {
    user: ['create', 'read', 'list', 'update'],
    product: ['read', 'list'],
    end_customer_order: ['create', 'read', 'list'],
    document: ['read', 'list'],
    invoice: ['read', 'list'],
    claim: ['create', 'read', 'list'],
    task: ['read', 'list', 'update'],
    // Klients pieprasa cenu (create) + apstiprina piedāvājumu (confirm). Redz tikai projicēto piedāvājumu.
    offer: ['create', 'read', 'list', 'confirm'],
    terms: ['read', 'list'],
    cost_component: ['read', 'list'],
  },
  customer_buyer: {
    product: ['read', 'list'],
    end_customer_order: ['create', 'read', 'list'],
    document: ['read', 'list'],
    invoice: ['read', 'list'],
    claim: ['create', 'read', 'list'],
    offer: ['create', 'read', 'list', 'confirm'],
    cost_component: ['read', 'list'],
  },
  // Pārvadātājs (4th party). Redz TIKAI savus norīkojumus; crossdock režīmā — anonimizēta projekcija (§7).
  carrier_admin: {
    user: ['create', 'read', 'list', 'update'],
    carrier: ['read', 'list', 'update'],
    supply_order: ['read', 'list', 'confirm', 'update'],
    document: ['create', 'read', 'list'],
    claim: ['read', 'list'],
    task: ['read', 'list', 'update'],
    terms: ['read', 'list', 'update'],
  },
  carrier_ops: {
    carrier: ['read', 'list'],
    supply_order: ['read', 'list', 'confirm'],
    document: ['create', 'read', 'list'],
    task: ['read', 'list', 'update'],
  },
  auditor: {
    audit_log: ['read', 'list'],
    end_customer_order: ['read', 'list'],
    supply_order: ['read', 'list'],
    document: ['read', 'list'],
    invoice: ['read', 'list'],
    claim: ['read', 'list'],
    compliance: ['read', 'list'],
    offer: ['read', 'list'],
    carrier: ['read', 'list'],
    cost_component: ['read', 'list'],
  },
};

/** Vai lomai ir atļauta darbība uz resursu (`manage` ietver visu). */
export function can(role: Role, resource: Resource, action: Action): boolean {
  const allowed = MATRIX[role]?.[resource];
  if (!allowed) return false;
  return allowed.includes('manage') || allowed.includes(action);
}

/**
 * Lauku līmeņa redakcija (§4.9, §6). Cenu konfidencialitāte:
 * Piegādātāja neto cenas/atlaides/MFC NEKAD nav redzamas gala klientam;
 * gala klients redz tikai platformas cenu.
 */
const REDACTIONS: Partial<Record<Role, Partial<Record<Resource, ReadonlyArray<string>>>>> = {
  customer_admin: {
    invoice: ['supplierNetPrice', 'supplierDiscount', 'mfc', 'pricingSnapshotId', 'fromTenantId'],
    end_customer_order: ['supplierNetPrice', 'mfc'],
    pricing: ['supplierNetPrice', 'mfc'],
    // Izmaksu komponentes: margin/piegādātāja neto/MFC/admin NEKAD gala klientam zem open-book (§4.9).
    cost_component: ['margin', 'marginPct', 'supplierNetPrice', 'mfc', 'adminCost'],
    offer: [
      'margin',
      'marginPct',
      'supplierNetPrice',
      'supplierTenantId',
      'supplierName',
      'costComponents',
    ],
  },
  customer_buyer: {
    invoice: ['supplierNetPrice', 'supplierDiscount', 'mfc', 'pricingSnapshotId', 'fromTenantId'],
    end_customer_order: ['supplierNetPrice', 'mfc'],
    pricing: ['supplierNetPrice', 'mfc'],
    cost_component: ['margin', 'marginPct', 'supplierNetPrice', 'mfc', 'adminCost'],
    offer: [
      'margin',
      'marginPct',
      'supplierNetPrice',
      'supplierTenantId',
      'supplierName',
      'costComponents',
    ],
  },
  // Piegādātāja identitātes redakciju pret gala klientu (un otrādi) primāri
  // nodrošina datu projekcija (§7); šeit papildu lauku aizsargi.
};

export function redactedFields(role: Role, resource: Resource): ReadonlyArray<string> {
  return REDACTIONS[role]?.[resource] ?? [];
}

/** Atgriež objekta kopiju bez aizliegtajiem laukiem konkrētai lomai+resursam. */
export function redact<T extends Record<string, unknown>>(
  role: Role,
  resource: Resource,
  obj: T,
): Partial<T> {
  const drop = redactedFields(role, resource);
  if (drop.length === 0) return { ...obj };
  const copy: Record<string, unknown> = { ...obj };
  for (const f of drop) delete copy[f];
  return copy as Partial<T>;
}

// --- Lomu ↔ tenanta tipa saderība + lomu piešķiršana (§5, §6) ---

/** Lomas, kas drīkst piederēt dotā tipa tenantam (`auditor` sēž platformas tenantā). */
const ROLE_TENANT_PREFIX: Record<TenantType, string> = {
  platform: 'platform_',
  supplier: 'supplier_',
  customer: 'customer_',
  carrier: 'carrier_',
};

export function rolesForTenantType(type: TenantType): ReadonlyArray<Role> {
  return ROLES.filter((r) => roleFitsTenantType(r, type));
}

export function roleFitsTenantType(role: Role, type: TenantType): boolean {
  if (role === 'auditor') return type === 'platform';
  return role.startsWith(ROLE_TENANT_PREFIX[type]);
}

/**
 * Lomu piešķiršanas ierobežojumi (vienkāršots): platform_super/platform_admin → jebkura loma;
 * supplier_admin → supplier_*; customer_admin → customer_*; carrier_admin → carrier_*; citi — neko.
 */
const ASSIGNABLE_ROLES: Partial<Record<Role, ReadonlyArray<Role>>> = {
  platform_super: ROLES,
  platform_admin: ROLES,
  supplier_admin: ['supplier_ops', 'supplier_admin'],
  customer_admin: ['customer_buyer', 'customer_admin'],
  carrier_admin: ['carrier_ops', 'carrier_admin'],
};

export function assignableRoles(actor: Role): ReadonlyArray<Role> {
  return ASSIGNABLE_ROLES[actor] ?? [];
}

export function canAssignRole(actor: Role, target: Role): boolean {
  return assignableRoles(actor).includes(target);
}
